import { action, extendObservable } from 'mobx';
import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import NotificationService from '../../../core/services/NotificationService';
import { isRequired } from '../../../core/validations';
import { browserHistory } from 'react-router';

export default class CharacterFormService {
  fetch = new FetchService();
  submit = new FetchService();
  fetchPreview = new FetchService();
  form = new FormService();

  constructor() {
    extendObservable(this, {
      characterId: null,
      blockUpdate: false,
      sampleImage: null,
      voices: [],
      isVoiceSearchModalOpen: false,
    });
    this.form.validations = {
      name: [isRequired],
      academicPlan: [isRequired],
      accent: [isRequired],
      elevenLabsVoiceId: [isRequired],
      imagePrompt: [isRequired],
    };
  }

  // The description is extracted from the reference image on upload, so it is only typed in by hand when there
  // is no image to derive it from.
  syncImagePromptValidation() {
    const referenceImage = this.form.getValue('referenceImage');
    this.form.validations.imagePrompt = referenceImage instanceof File ? [] : [isRequired];
  }

  handleLoad = action((characterId) => {
    this.form.reset();
    if (characterId) {
      this.fetch
        .fetch({
          url: `/characters/${characterId}/update`,
          method: 'HEAD',
          apiVersion: 'v3',
        })
        .then(() => {
          this.blockUpdate = !!this.fetch.error;
        })
        .finally(() => {
          this.fetch
            .fetch({
              url: `/characters/${characterId}`,
              apiVersion: 'v3',
            })
            .then(() => {
              if (this.fetch.data) {
                this.form.setInitialValues({
                  ...this.fetch.data,
                });

                this.fetch
                  .fetch({
                    url: `/characters/${characterId}/voice`,
                    apiVersion: 'v3',
                  })
                  .then(() => {
                    if (this.fetch.data) {
                      this.form.setValue('voiceData', this.fetch.data);
                      this.form.setValue('elevenLabsVoiceId', this.fetch.data.id);
                      this.form.setValue('elevenLabsVoicePreview', this.fetch.data.previewUrl);
                    }
                  });
              }
            });
        });
    }
    this.characterId = characterId;
  });

  buildFormData = (fields) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([field, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        formData.append(field, value);
      }
    });

    return formData;
  };

  handleSubmit = action(() => {
    this.syncImagePromptValidation();
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }
    const characterId = this.form.getValue('id');
    this.submit
      .fetch({
        method: characterId ? 'put' : 'post',
        url: characterId ? `/characters/${characterId}` : '/characters',
        apiVersion: 'v3',
        body: this.buildFormData({
          name: this.form.getValue('name'),
          academicPlan: this.form.getValue('academicPlan'),
          accent: this.form.getValue('accent'),
          elevenLabsVoiceId: this.form.getValue('elevenLabsVoiceId'),
          imagePrompt: this.form.getValue('imagePrompt'),
          referenceImage: this.form.getValue('referenceImage'),
        }),
      })
      .then(() => {
        if (this.submit.data) {
          browserHistory.push('/characters');
          NotificationService.addNotification(
            `Character ${characterId ? 'updated' : 'created'} successfully.`,
            'success'
          );
        }
        if (this.submit.error) {
          NotificationService.addNotification(`Error ${characterId ? 'updating' : 'creating'} character.`, 'error');
        }
      });
  });

  handlePreviewImage = action(() => {
    this.fetchPreview
      .fetch({
        url: '/characters/image-sample',
        method: 'post',
        apiVersion: 'v3',
        body: this.buildFormData({
          prompt: this.form.getValue('imagePrompt'),
          referenceImage: this.form.getValue('referenceImage'),
        }),
        responseType: 'blob',
      })
      .then(() => {
        if (this.fetchPreview.data) {
          this.sampleImage = URL.createObjectURL(this.fetchPreview.data);
        }
        if (this.fetchPreview.error) {
          NotificationService.addNotification(`Error generating character sample image.`, 'error');
        }
      });
  });

  handlePreviewEnd = action(() => {
    URL.revokeObjectURL(this.sampleImage);
    this.sampleImage = null;
  });

  handleSearchVoices = action(({ accent, criteria, age, gender } = { accent: 'english' }) => {
    this.isVoiceSearchModalOpen = true;

    this.fetch
      .fetch({
        url: `/characters/${accent}/voices`,
        method: 'GET',
        apiVersion: 'v3',
        params: {
          criteria,
          age,
          gender,
        },
      })
      .then(() => {
        this.voices = this.fetch.data;
      })
      .catch(() => {
        this.voices = this.fetch.data = [];
      });
  });

  handleSelectVoice = action((data) => {
    this.isVoiceSearchModalOpen = false;
    this.form.setValue('elevenLabsVoiceId', data.voiceId);
    this.form.setValue('elevenLabsVoicePreview', data.previewUrl);
    this.voices = [];
  });
}
